import { useState } from 'react';
import { CalendarDaysIcon, CreditCardIcon } from '@heroicons/react/24/outline';
import {
  currencyFormatter,
  IPaymentPlan,
  toDecimal,
} from 'shared';

import { Drawer } from '../../Atoms/Drawer';
import { PillBadge } from '../../Atoms/PillBadge';
import { DatePopover } from '../DatePopover';
import { classNames } from '../../lib/classNames';

import {
  getInstalmentStatus,
  InstalmentStatusColor,
  InvoicePaymentPlanDisplay,
} from './InvoicePaymentPlanDisplay';

const tileStyles: Record<InstalmentStatusColor, { bar: string; border: string }> = {
  green: { bar: `bg-green-500`, border: `border-gray-200` },
  red: { bar: `bg-red-400`, border: `border-red-200` },
  yellow: { bar: `bg-yellow-400`, border: `border-gray-200` },
  blue: { bar: `bg-blue-200`, border: `border-gray-200` },
};

/** Up to 4 instalments show in full. Longer plans show 3 from the first unpaid one, plus a "+ more" tile */
export function instalmentTileWindow(colors: InstalmentStatusColor[]) {
  if (colors.length <= 4) {
    return { start: 0, end: colors.length, more: 0 };
  }

  const firstUnpaid = colors.findIndex(color => color !== `green`);
  const start = firstUnpaid === -1 ? colors.length - 3 : Math.min(firstUnpaid, colors.length - 3);

  return { start, end: start + 3, more: colors.length - 3 };
}

function plural(count: number, word: string) {
  return count ? `${count} ${word}` : null;
}

interface PaymentPlanStripProps {
  plan: IPaymentPlan;
  currency: string;
  timezone: string;
  id?: string;
  /** Right side of the strip header */
  actions?: React.ReactNode;
  /** Shown under the full schedule in the drawer */
  footnote?: string;
  onRemovePlan?: () => void;
  /** Adds a pay button to this instalment's tile and the schedule drawer */
  payInstalment?: { number: number; label: string; onClick: () => void };
}

/** Summary of an active payment plan at the top of the invoice card (design 8a) */
export function PaymentPlanStrip({ plan, currency, timezone, id, actions, footnote, onRemovePlan, payInstalment }: PaymentPlanStripProps) {
  const [scheduleOpen, setScheduleOpen] = useState(false);

  const format = currencyFormatter(currency);
  const rows = (plan.instalments || []).map((instalment, index) => ({
    instalment,
    number: index + 1,
    status: getInstalmentStatus(instalment),
  }));
  const totalCents = rows.reduce((sum, r) => sum + (r.instalment.amountCents || 0), 0);
  const paidCents = rows.reduce((sum, r) => sum + (r.instalment.amountCentsPaid || 0), 0);
  const overdueCents = rows
    .filter(r => r.status.color === `red`)
    .reduce((sum, r) => sum + (r.instalment.amountCents || 0) - (r.instalment.amountCentsPaid || 0), 0);
  const count = (color: InstalmentStatusColor) => rows.filter(r => r.status.color === color).length;
  const counts = [
    plural(count(`green`), `paid`),
    plural(count(`red`), `overdue`),
    plural(count(`yellow`), `partially paid`),
    plural(count(`blue`), `scheduled`),
  ].filter(Boolean).join(` · `);

  const { start, end, more } = instalmentTileWindow(rows.map(r => r.status.color));
  const tiles = rows.slice(start, end);
  const columns = Math.min(tiles.length + (more ? 1 : 0), 4);
  const pct = (cents: number) => `${Math.min(100, (cents / (totalCents || 1)) * 100)}%`;

  return (
    <div
      id={ id }
      className={ `@container flex flex-col gap-3 border-b border-gray-200 bg-gray-50 px-6 py-[1.125rem]` }
    >
      <div className={ `flex flex-wrap items-center justify-between gap-x-4 gap-y-2` }>
        <span className={ `flex flex-wrap items-center gap-2 text-sm font-semibold text-gray-900` }>
          <CalendarDaysIcon className={ `h-4 w-4 text-paidniceGreen-600` } />
          { `Payment plan` }
          <span className={ `text-[0.8125rem] font-normal text-gray-500` }>
            { `· Paid ${format(toDecimal(paidCents))} of ${format(toDecimal(totalCents))}${counts ? ` · ${counts}` : ``}` }
          </span>
        </span>
        { actions ? (
          <span className={ `flex flex-wrap items-center gap-3` }>
            { actions }
          </span>
        ) : null }
      </div>

      <div className={ `flex h-1.5 overflow-hidden rounded-full bg-gray-200` }>
        <span
          className={ `bg-green-500` }
          style={ { width: pct(paidCents) } }
        />
        <span
          className={ `bg-red-400` }
          style={ { width: pct(overdueCents) } }
        />
      </div>

      <div
        className={ `grid grid-cols-2 gap-2 @lg:grid-cols-[repeat(var(--plan-cols),minmax(0,1fr))]` }
        style={ { [`--plan-cols`]: columns } as React.CSSProperties }
      >
        { tiles.map(({ instalment, number, status }) => (
          <div
            key={ instalment.id }
            className={ classNames(
              `relative flex min-w-0 flex-col gap-1.5 overflow-hidden rounded-[0.625rem] border bg-white px-3 py-2.5`,
              tileStyles[status.color].border,
            ) }
          >
            <span className={ classNames(`absolute inset-x-0 top-0 h-[0.1875rem]`, tileStyles[status.color].bar) } />
            <div className={ `flex flex-wrap items-center justify-between gap-x-2 gap-y-1` }>
              <span className={ `text-xs text-gray-500` }>{ `${number} of ${rows.length}` }</span>
              <PillBadge
                size={ `xs` }
                color={ status.color }
                bordered
              >
                { status.label }
              </PillBadge>
            </div>
            <span className={ `truncate text-[0.9375rem] font-semibold tabular-nums text-gray-900` }>
              { format(toDecimal(instalment.amountCents || 0)) }
            </span>
            <span className={ `truncate text-[0.8125rem] text-gray-700` }>
              { instalment.dueDate ? (
                <DatePopover
                  disablePopover
                  date={ instalment.dueDate }
                  labelFormat={ `ccc d LLL yyyy` }
                  organisationTimezone={ timezone }
                />
              ) : `No due date` }
            </span>
            { payInstalment?.number === number ? (
              <button
                type={ `button` }
                onClick={ payInstalment.onClick }
                className={ `mt-1 flex h-7 w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-gray-300 bg-white px-2.5 text-xs font-medium text-gray-900 shadow-sm hover:bg-gray-50` }
              >
                <CreditCardIcon className={ `h-4 w-4 text-gray-500` } />
                { payInstalment.label }
              </button>
            ) : null }
          </div>
        )) }
        { more ? (
          <button
            type={ `button` }
            onClick={ () => setScheduleOpen(true) }
            className={ `flex cursor-pointer flex-col justify-center gap-1 rounded-[0.625rem] border border-dashed border-gray-300 px-3 py-2.5 text-left hover:border-paidniceGreen-200 hover:bg-white` }
          >
            <span className={ `text-[0.9375rem] font-semibold text-gray-900` }>{ `+ ${more} more` }</span>
            <span className={ `text-[0.8125rem] text-paidniceGreen-600` }>{ `View full schedule →` }</span>
          </button>
        ) : null }
      </div>

      <Drawer
        open={ scheduleOpen }
        setOpen={ setScheduleOpen }
        title={ `Payment plan schedule` }
        size={ `md` }
      >
        <InvoicePaymentPlanDisplay
          invoiceCurrency={ currency }
          plan={ plan }
          onRemovePlan={ onRemovePlan }
          onPayNextInstalment={ payInstalment && (() => {
            setScheduleOpen(false);
            payInstalment.onClick();
          }) }
          timezone={ timezone }
        />
        { footnote ? <p className={ `text-xs text-gray-500` }>{ footnote }</p> : null }
      </Drawer>
    </div>
  );
}
